import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import type * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

export const AsyncServiceSelect = ({
  onChange,
  value,
  required,
}: FieldExtensionComponentProps<string>) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [services, setServices] = useState<string[]>([]);
  const [selectedService, setSelectedService] = useState<string>(value || '');
  const [error, setError] = useState<string | null>(null);

  const fetchServices = useCallback(async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('edm-connector');
      const response = await fetchApi.fetch(`${baseUrl}/service`);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to fetch services: ${response.statusText}${errorText && errorText.trim() ? ` (${errorText.trim().slice(0, 200)})` : ''}`
        );
      }

      const serviceList = await response.json();
      setServices(Array.isArray(serviceList) ? serviceList : []);
      setError(null);
    } catch (err) {
      setError('Failed to fetch services.');
    }
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newService = event.target.value as string;
    setSelectedService(newService);
    onChange?.(newService);
  };

  return (
    <FormControl margin="normal" required={required} error={!!error} fullWidth>
      <InputLabel id="service-select-label">Service</InputLabel>
      <Select labelId="service-select-label" value={selectedService} onChange={handleChange}>
        {services.map((service) => (
          <MenuItem key={service} value={service}>
            {service}
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText error>{error}</FormHelperText>}
      {!error && <FormHelperText>Select a service from the list.</FormHelperText>}
    </FormControl>
  );
};